/**
 * The Purchase order detail component. Renders the purchase order details as
 * well as the line items on the purchase order. There is also a button to
 * accept the purchase order in NetSuite, and download a pdf version of the PO
 */
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  Button,
  Icon,
  HideText,
  Stepper,
} from '@fashionnovateam/nova-ui-toolkit';
import { useParams } from 'react-router-dom';
import {
  useGetUserPurchaseOrder,
  useAcceptPurchaseOrder,
  useUpdatePoBulkProductionSubStage,
  useUploadInvoice,
  useUploadPackingList,
  useSetProp65,
} from '../../Lib/Hooks/purchase-orders.hooks';
import styles from './PurchaseOrderDetail.module.css';
import ConfirmationModal from '../ReusableComponents/Modals/ConfirmationModal/ConfirmationModal';
import AuditSection from './AuditSection/AuditSection';
import BasicModal from '../ReusableComponents/Modals/BasicModal/BasicModal';
import DimensionsModal from '../FnCatalogV2/DimensionsModal/DimensionsModal';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';
import PrinterConfiguration from '../ReusableComponents/PrinterConfiguration/PrinterConfiguration';
import Page from '../Page';
import Header from '../Header';
import { PoCard } from '../PoCard';
import PurchaseOrderDetailDataGrid from './PurchaseOrderDetailDataGrid';
import getPoStatus from '../../Lib/Utils/Helpers/make-button-text';
import PoStatus, {
  PurchaseOrderStage,
} from '../../Lib/Constants/purchase-order-status';
import { CatalogItem, PurchaseOrderLine } from '../../Lib/Types/purchase-order';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles';
import { PurchaseOrderNotFound } from './PurchaseOrderNotFound';
import { isPrintingApproved } from '../../Lib/Utils/Asn/printingApproved';
import { User } from '../../Lib/Api/vendor';
import { Prop65Drawer } from './Prop65Drawer';
import toast from '../Toast/Toast';
import { useCurrentUser } from '../../Lib/Contexts/CurrentUserContext';
import { PurchaseOrderActivities } from './PurchasrOrderActivities/PurchaseOrderActivities';
import { DocumentCenter } from './DocumentCenter';

export type PurchaseOrderDetailsProps = {
  user: User;
  rejectPurchaseOrder: (
    userId: string,
    purchaseOrderId: string,
    message: string,
  ) => void;
};

export default function PurchaseOrderDetail(props: PurchaseOrderDetailsProps) {
  const { flags } = React.useContext(FeatureFlagsContext);
  const { currentUser } = useCurrentUser();
  const { purchaseOrderId, id: currentUserId } = useParams<{
    purchaseOrderId: string;
    id: string;
  }>();
  const { isLoading, isError, error, data, refetch } = useGetUserPurchaseOrder(
    currentUserId,
    purchaseOrderId,
  );
  const {
    mutate: acceptPurchaseOrderMutation,
    isLoading: isAcceptPurchaseOrderLoading,
  } = useAcceptPurchaseOrder();

  const {
    mutate: PoBulkProductionSubStageMutation,
    isLoading: isPoBulkProductionStatusLoading,
  } = useUpdatePoBulkProductionSubStage();

  const {
    uploadInvoice,
    progress: invoiceUploadProgress,
    isLoading: isUploadInvoiceLoading,
  } = useUploadInvoice();

  const {
    uploadPackingList,
    progress: packingListUploadProgress,
    isLoading: isUploadPackingListLoading,
  } = useUploadPackingList();

  const { mutateAsync: setProp65 } = useSetProp65();
  const { user } = props;

  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [dimsModalOpen, setDimsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [invoiceUploaded, setInvoiceUploaded] = useState(false);
  const [packingListUploaded, setPackingListUploaded] = useState(false);
  const [isProp65DrawerOpen, setIsProp65DrawerOpen] = useState(false);

  const isPrintingAllowed = isPrintingApproved(user, currentUser);
  const loading =
    isLoading ||
    isAcceptPurchaseOrderLoading ||
    isUploadInvoiceLoading ||
    isUploadPackingListLoading ||
    isPoBulkProductionStatusLoading;

  const queryClient = useQueryClient();

  useEffect(() => {
    if (data) {
      setInvoiceUploaded(data.purchaseOrder.invoice_pdf.exists);
      setPackingListUploaded(data.purchaseOrder.packing_list.exists);
    }
  }, [data]);

  const perms = useMemo(() => {
    const canAccept = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'ACCEPT_PO',
    );
    const canPrint = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'PRINT',
    );
    /*
     * This is a special permission for schedulers and pd to print POs without vendor acceptance
     * This is get permission from currentUser (user logged in) vs user (user on url)
     */
    const canPrintWithoutVendorAcceptance = hasPermissionTo(
      'users/:id/purchase-orders',
      currentUser?.role || user.role,
      Actions.UPDATE,
      'PRINT_WITHOUT_VENDOR_ACCEPTANCE',
    );
    const canUpdateInvoice = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'INVOICE',
    );
    const canUpdatePackingList = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'PACKING_LIST',
    );
    const canDownloadDetails = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.READ,
      'DETAILS',
    );
    const canUpdateSizeAndWeight = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'SIZE_AND_WEIGHT',
    );
    const canReject = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'REJECT',
    );
    const canDownloadInvoice = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.READ,
      'INVOICE',
    );
    const canDownloadPackingList = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.READ,
      'PACKING_LIST',
    );
    const canUpdateBulkStatus = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'STATUS',
    );
    const canViewCost = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.READ,
      'VIEW_COST',
    );
    const canCreateRevision = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'CREATE_REVISION',
    );
    const canUploadTestingReports = hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'TESTING_REPORTS',
    );

    return {
      canAccept,
      canPrint,
      canPrintWithoutVendorAcceptance,
      canUpdateInvoice,
      canUpdatePackingList,
      canDownloadDetails,
      canUpdateSizeAndWeight,
      canReject,
      canDownloadInvoice,
      canDownloadPackingList,
      canUpdateBulkStatus,
      canViewCost,
      canCreateRevision,
      canUploadTestingReports,
    };
  }, [user]);

  if (isError)
    return (
      <PurchaseOrderNotFound purchaseOrderId={purchaseOrderId} error={error} />
    );

  if (isLoading || !data) return <div>Loading...</div>;
  const { purchaseOrder } = data;

  const {
    po_number: poNumber,
    invoice_pdf: invoicePdf,
    po_pdf: poPdf,
    delivery_reference: deliveryReference,
    audits,
  } = purchaseOrder;

  if (!poNumber) {
    return (
      <div>
        <div>No Purchase Order Found.</div>
      </div>
    );
  }

  const acceptButtonText = getPoStatus({
    revised_at: purchaseOrder.revised_at,
    vendor_rejection_reason: purchaseOrder.vendor_rejection_reason,
    revision_accepted_at: purchaseOrder.revision_accepted_at,
  });

  const isVendorAccepted =
    acceptButtonText === PoStatus.ACCEPTED ||
    acceptButtonText === PoStatus.PENDING;

  /**
   * Grabs the link for the pdf and formats the blob to open in a new window
   */
  const handleDownload = () => {
    const {
      po_pdf: { read },
    } = purchaseOrder;
    window.open(read);
  };

  /**
   * Handles the file being selected for upload, and calls uploadInvoice
   *
   * @param {string} name the input name
   * @param {object} file the file
   */
  const updateInvoiceField = async (file: any) => {
    const formData = { invoice_pdf: file };

    await uploadInvoice(currentUserId, purchaseOrderId, formData);
    refetch();
  };

  /**
   * Handles the file being selected for upload, and calls uploadPackingList
   *
   * @param {string} name the input name
   * @param {object} file the file
   */
  const updatePackingList = async (file: any) => {
    const formData = { packing_list: file };

    await uploadPackingList(currentUserId, purchaseOrderId, formData);
    refetch();
  };

  /**
   * Toggles the local state for opening and closing the accept modal
   */
  const toggleAcceptModal = () => {
    setAcceptModalOpen(!acceptModalOpen);
  };

  /**
   * Toggles the local state for opening and closing the reject modal
   */
  const toggleRejectModal = () => {
    setRejectModalOpen(!rejectModalOpen);
  };

  /**
   * Toggles the local state for opening and closing the dims modal
   */
  const toggleDimsModal = () => {
    setDimsModalOpen(!dimsModalOpen);
    setSelectedItem(selectedItem);
  };

  const openDimsModal = (item: any) => {
    setSelectedItem(item);
    setDimsModalOpen(true);
  };

  /**
   * A function passed down to the confirmation modal to call
   * acceptPurchaseOrder to accept the transaction, and close the modal
   * afterwards.
   */
  const acceptFromModal = () => {
    acceptPurchaseOrderMutation({
      userId: currentUserId,
      purchaseOrderId,
    });
    setTimeout(() => {
      toggleAcceptModal();
      refetch();
    }, 400);
  };

  /**
   * A function passed down to the confirmation modal to call
   * rejectPurchaseOrder to accept the transaction, and close the modal
   * afterward
   */
  const rejectFromModal = (message: any) => {
    const { rejectPurchaseOrder } = props;
    rejectPurchaseOrder(currentUserId, purchaseOrderId, message);
    setTimeout(() => {
      toggleRejectModal();
      refetch();
    }, 500);
  };

  const disableListUpload = !deliveryReference;

  const transformedStages = Object.values(PurchaseOrderStage).map((stage) => ({
    label: `${stage}`,
  }));
  const stageIndex = Object.values(PurchaseOrderStage).indexOf(
    purchaseOrder.stage,
  );

  const handleProp65DrawerOpen = () => {
    setIsProp65DrawerOpen(true);
  };

  const handleProp65DrawerClose = () => {
    setIsProp65DrawerOpen(false);
  };

  const handleProp65Save = async (
    prop65Values: Record<string, 'yes' | 'no'>,
  ): Promise<void> => {
    try {
      const updates = purchaseOrder.purchase_order_lines
        .filter(
          (line: PurchaseOrderLine) =>
            prop65Values[line.po_line_id] !== undefined,
        )
        .map((line: PurchaseOrderLine) => ({
          lineId: Number(line.po_line_id),
          prop65: prop65Values[line.po_line_id] === 'yes',
        }));

      await setProp65({
        userId: currentUserId,
        poNumber: purchaseOrder.po_number,
        updates,
      });

      toast('CA Prop 65 warning successfully updated.', {
        type: 'success',
      });
      handleProp65DrawerClose();

      setTimeout(() => {
        queryClient.invalidateQueries(['purchase-order', currentUserId]);
      }, 300);
    } catch (errorProp65) {
      toast('Failed to update CA Prop 65 warning.', { type: 'error' });
      throw errorProp65;
    }
  };

  return (
    <Page.Container>
      <Header.Major
        onCloseLink={`/users/${currentUserId}/purchase-orders`}
        icon="file/file-text"
        title="Purchase Orders"
        subTitle={poNumber}
      >
        {get(poPdf, 'exists') && perms.canDownloadDetails && (
          <Button
            id="po-detail-download"
            size="sm"
            color="secondary"
            onClick={handleDownload}
          >
            <Icon name="file-02/file-02-pdf-format" />
            <HideText notAbove="lg">Download</HideText>
          </Button>
        )}
        {isPrintingAllowed && perms.canPrint && <PrinterConfiguration />}
      </Header.Major>
      <Header.Minor className="overflow-scroll">
        <Stepper
          activeStep={stageIndex}
          steps={transformedStages}
          disabled={
            flags.cancelled_po &&
            flags.cancelled_po.enabled &&
            purchaseOrder.is_cancelled
          }
        />
      </Header.Minor>
      <Page.Content>
        <PoCard
          details
          purchaseOrder={purchaseOrder}
          userId={currentUserId}
          loading={loading}
          onProp65DrawerOpen={handleProp65DrawerOpen}
          onAcceptPurchaseOrders={() => {
            acceptPurchaseOrderMutation({
              userId: currentUserId,
              purchaseOrderId,
            });
          }}
          onRejectPurchaseOrders={props.rejectPurchaseOrder}
          onConfirmPrint={() => refetch()}
          onPoSubStageUpdate={(sub_stage) => {
            PoBulkProductionSubStageMutation({
              userId: currentUserId,
              purchaseOrderId,
              sub_stage,
            });
          }}
          perms={perms}
          user={props.user}
        />

        <PurchaseOrderDetailDataGrid
          userId={currentUserId}
          purchaseOrder={purchaseOrder}
          refetch={refetch}
          onDimensionOpen={openDimsModal}
          isPoVendorAccepted={isVendorAccepted}
          perms={perms}
          merchType={props?.user?.vendor?.merch_type || ''}
          user={props?.user}
          onProp65DrawerOpen={handleProp65DrawerOpen}
        />
        <p className={styles.subText}>
          Updates will not be reflected for up to 5 minutes
        </p>
        {flags.hide_audit_status_column &&
        flags.hide_audit_status_column.enabled ? null : (
          <AuditSection audits={audits} />
        )}
        <div>
          <DocumentCenter
            userId={currentUserId}
            poNumber={poNumber}
            disabled={loading}
            isCancelled={purchaseOrder.is_cancelled}
            invoice={{
              canUpdate: perms.canUpdateInvoice,
              canDownload: perms.canDownloadInvoice,
              isUploaded: invoiceUploaded,
              uploadProgress: invoiceUploadProgress,
              onUpload: updateInvoiceField,
              onDownload: () => window.open(invoicePdf.read),
              onRemove: () => setInvoiceUploaded(false),
            }}
            packingList={{
              canUpdate: perms.canUpdatePackingList,
              canDownload: perms.canDownloadPackingList,
              isUploaded: packingListUploaded,
              uploadProgress: packingListUploadProgress,
              isDisabled: disableListUpload,
              onUpload: updatePackingList,
              onDownload: () => window.open(purchaseOrder.packing_list.read),
              onRemove: () => setPackingListUploaded(false),
            }}
            canUploadTestingReports={perms.canUploadTestingReports}
          />
          {props?.user?.vendor && (
            <PurchaseOrderActivities
              purchaseOrder={purchaseOrder}
              vendor={props.user.vendor}
              userId={`${props.user.id}`}
              userRole={props.user.role}
            />
          )}
        </div>
        <Prop65Drawer
          isOpen={isProp65DrawerOpen}
          onClose={handleProp65DrawerClose}
          onSave={handleProp65Save}
          loading={loading}
          purchaseOrder={purchaseOrder}
        />
      </Page.Content>
      <ConfirmationModal
        open={acceptModalOpen}
        onClose={toggleAcceptModal}
        onConfirm={acceptFromModal}
        loading={loading}
        modalId="accept-po-modal"
      />
      <ConfirmationModal
        messageTitle="Add A Rejection Reason"
        open={rejectModalOpen}
        onClose={toggleRejectModal}
        onConfirm={rejectFromModal}
        loading={loading}
        withMessage
        modalId="reject-po-modal"
      />
      {dimsModalOpen && selectedItem !== null ? (
        <BasicModal
          open={dimsModalOpen}
          onClose={toggleDimsModal}
          title="Size + Weight"
        >
          <DimensionsModal
            selectedItemSkus={purchaseOrder.purchase_order_lines.map(
              (line: PurchaseOrderLine) => line.sku,
            )}
            defaultSelectedItem={selectedItem}
            onClose={toggleDimsModal}
            onSave={() => {
              refetch();
            }}
          />
        </BasicModal>
      ) : null}
    </Page.Container>
  );
}
